import { useAppSelector } from '@/hooks/store';
import { selectCurrentUser } from '@/store/features/users';
import { posthogConfig } from '@/utils/posthog';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type PosthogStatus = {
  isPosthogReady: boolean;
  isPosthogIdentified: boolean;
};

type PosthogActions = {
  setIsPosthogIdentified: Dispatch<SetStateAction<boolean>>;
};

const PosthogStatusContext = createContext<PosthogStatus>({
  isPosthogReady: false,
  isPosthogIdentified: false,
});
const PosthogActionsContext = createContext<PosthogActions>({
  setIsPosthogIdentified: () => false,
});

export const usePosthogStatus = () => useContext(PosthogStatusContext);
export const usePosthogActions = () => useContext(PosthogActionsContext);

export const PosthogTrackingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const user = useAppSelector(selectCurrentUser);
  const [isPosthogReady, setIsPosthogReady] = useState<boolean>(false);
  const [isPosthogIdentified, setIsPosthogIdentified] =
    useState<boolean>(false);
  const posthogStatus = useMemo<PosthogStatus>(
    () => ({
      isPosthogReady: isPosthogReady,
      isPosthogIdentified: isPosthogIdentified,
    }),
    [isPosthogReady, isPosthogIdentified],
  );
  const posthogActions = useMemo<PosthogActions>(
    () => ({
      setIsPosthogIdentified: setIsPosthogIdentified,
    }),
    [],
  );

  useEffect(() => {
    if (!user) {
      setIsPosthogReady(false);
      setIsPosthogIdentified(false);
      posthog.reset();
      return;
    }

    const { posthogProjectToken, posthogOptions } = posthogConfig(user);
    const allowTracking = !!user.account.allowPosthogTracking;

    if (!posthogProjectToken) {
      setIsPosthogReady(false);
      setIsPosthogIdentified(false);
      return;
    }

    setIsPosthogIdentified(false);

    if (posthog.__loaded) {
      posthog.set_config(posthogOptions);
      if (posthog.has_opted_out_capturing()) {
        posthog.opt_in_capturing({
          captureEventName: false,
        });
      }
      setIsPosthogReady(allowTracking);
      return;
    }

    setIsPosthogReady(false);

    posthog.init(posthogProjectToken, {
      ...posthogOptions,
      loaded: (posthogClient) => {
        if (posthogClient.has_opted_out_capturing()) {
          posthog.opt_in_capturing({
            captureEventName: false,
          });
        }
        posthogClient.register({ service: 'frontend' });
        if (process.env.REACT_APP_RELEASE_VERSION) {
          posthogClient.register({
            $release: process.env.REACT_APP_RELEASE_VERSION,
          });
        }
        posthogOptions.loaded?.(posthogClient);
        setIsPosthogReady(allowTracking);
      },
    });
  }, [user?.id, user?.account?.id, user?.account?.allowPosthogTracking]);

  return (
    <PostHogProvider client={posthog}>
      <PosthogActionsContext.Provider value={posthogActions}>
        <PosthogStatusContext.Provider value={posthogStatus}>
          {children}
        </PosthogStatusContext.Provider>
      </PosthogActionsContext.Provider>
    </PostHogProvider>
  );
};
